import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  ImageList,
  ImageListItem,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const clampIndex = (i, len) => ((i % len) + len) % len;

const TaskImageGalleryModal = ({
  open,
  onClose,
  images = [],
  startIndex = 0,
  title = "Task Images",
}) => {
  const safeImages = useMemo(
    () => (Array.isArray(images) ? images.filter(Boolean) : []),
    [images]
  );
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (open) setIdx(clampIndex(startIndex, safeImages.length || 1));
  }, [open, startIndex, safeImages.length]);

  const go = useCallback(
    (delta) => {
      if (!safeImages.length) return;
      setIdx((i) => clampIndex(i + delta, safeImages.length));
    },
    [safeImages.length]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, go, onClose]);

  const canNav = safeImages.length > 1;
  const currentSrc = safeImages[idx];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        {title}
        <Typography variant="body2" component="span" sx={{ ml: 1, opacity: 0.7 }}>
          {safeImages.length ? `${idx + 1} / ${safeImages.length}` : ""}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box position="relative" display="flex" justifyContent="center" alignItems="center" minHeight={360}>
          {currentSrc ? (
            <>
              <img
                src={currentSrc}
                alt={`Task image ${idx + 1}`}
                style={{
                  maxWidth: "100%",
                  maxHeight: "65vh",
                  objectFit: "contain",
                  borderRadius: 8,
                }}
                draggable={false}
              />

              {canNav && (
                <>
                  <IconButton
                    aria-label="previous"
                    onClick={() => go(-1)}
                    sx={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)" }}
                  >
                    <ArrowBackIosNewIcon />
                  </IconButton>
                  <IconButton
                    aria-label="next"
                    onClick={() => go(1)}
                    sx={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)" }}
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>
                </>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No images available
            </Typography>
          )}
        </Box>

        {/* Thumbnails */}
        {safeImages.length > 1 && (
          <Box mt={2}>
            <ImageList cols={Math.min(safeImages.length, 8)} gap={8} rowHeight={80}>
              {safeImages.map((src, i) => (
                <ImageListItem key={src} onClick={() => setIdx(i)} style={{ cursor: "pointer" }}>
                  <img
                    src={src}
                    alt={`thumb ${i + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: 8,
                      outline: i === idx ? "2px solid #00bcd4" : "none",
                    }}
                    draggable={false}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TaskImageGalleryModal;
